import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, ChevronLeft, ChevronRight, Copy, Download, FileSearch, Gauge, RefreshCw, Search, Send, X } from 'lucide-react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { billRunsService, type BillRunsData } from '../services/billRunsService';
import { bulkReportService } from '../services/bulkReportService';
import { billEmailService } from '../services/billEmailService';
import { missingBillService } from '../services/missingBillService';
import { NewBadge } from '../components/NewBadge';
import { buildBillRunRows, COVERAGE_LABELS, COVERAGE_ORDER, type BillRunRow } from '../lib/billRunRows';
import {
  ASSIGNED_LABELS, ASSIGNED_ORDER, BILL_RUN_DETAIL_LABELS, BILL_RUN_DETAIL_ORDER,
  BILL_RUN_STATUS_LABELS, BILL_RUN_STATUS_ORDER, billRunStatus, compareBillRunRows,
} from '../lib/billRunStatus';
import {
  ENROLMENT_LABELS, ENROLMENT_ORDER, filterRunSites, matchesBillRunConditions, matchesRunSiteFilters,
  type BillRunConditions, type BillRunSiteFilters, type VarianceDirection,
} from '../lib/billRunConditions';
import { computeMetrics } from '../lib/billRunMetrics';
import { driversFromRows } from '../lib/mismatchDrivers';
import { detailRow, monthMetrics, summaryRows } from '../lib/monthlyBillReport';
import { monthLabel } from '../lib/billMonths';
import { exportMultiSheetXLSX } from '../utils/exportUtils';
import { siteFilterOptions } from '../lib/siteFilters';
import { billRunsCsv, billRunsSheetRows } from '../lib/billRunsDisplay';
import { billRunsSummaryRows, outcomeDollars } from '../lib/billRunsSummary';
import { nextSort, sortBillRunRows } from '../lib/billRunsSort';
import {
  BILL_RUNS_TABS, EMPTY_VIEW, lastCompletedMonth, monthRange, readBillRunsView, writeBillRunsView,
  type BillRunsTab, type BillRunsView,
} from '../lib/billRunsUrl';
import { downloadCsv } from '../lib/csvText';
import { DEFAULT_MATCH_TOLERANCE_PCT, MATCH_TOLERANCE_OPTIONS, matchTolerance } from '../lib/reportFormulas';
import { canSend, NO_SENDS, reportFigures, SEND_STATUS_LABELS, type ReportFigures } from '../lib/reportStatus';
import { SEND_STATUS_ORDER } from '../lib/billFilters';
import { runDurationLabel } from '../lib/billRunLog';
import { MISSING_TAGS, MISSING_TAG_LABELS, MAX_NMI_MONTHS, type MissingTag } from '../lib/missingBills';
import { useBillReports } from '../hooks/useBillVerdicts';
import { useWorkflowStep } from '../hooks/useWorkflowStep';
import { BoardActionDialog, type BoardAction } from '../components/bills/BoardActionDialog';
import { DuplicatePeriodsDialog } from '../components/bills/DuplicatePeriodsDialog';
import { BulkSendReportsDialog } from '../components/bills/BulkSendReportsDialog';
import { SendBillReportDialog } from '../components/bills/SendBillReportDialog';
import { BillRunsBatches } from '../components/billRunsPage/BillRunsBatches';
import { BillRunsMetrics, type MetricGroup } from '../components/billRunsPage/BillRunsMetrics';
import { BillRunsTable } from '../components/billRunsPage/BillRunsTable';
import { MultiCheck } from '../components/billRunsPage/MultiCheck';
import { MismatchDriversPanel } from '../components/billRunsPage/MismatchDriversPanel';
import { AssignedStatusSelect } from '../components/billRunsPage/AssignedStatusSelect';
import { BillRunsBillActions } from '../components/billRunsPage/BillRunsBillActions';
import type { BvBill } from '../types/billTypes';

// Bill Runs — one page for bills, bill runs and customer reports
// (tasks/prd/prd-bill-runs.md). Pick months, filter, Fetch, read the cards, act
// on rows. Built only from new files: the Bills, Bulk Bill Run and Bulk Customer
// Report pages are untouched, and everything they do that this page does too
// (run, parse, set status, tag, send) calls the same services and dialogs.

const PAGE_SIZE = 50;

/** Which rows each tab shows. Reports: bills that have a customer report. */
const IN_TAB: Record<BillRunsTab, (r: BillRunRow) => boolean> = {
  bills: (r) => r.kind === 'bill',
  missing: (r) => r.kind === 'missing',
  reports: (r) => r.kind === 'bill' && billRunStatus(r).send !== 'no_report',
  insights: () => false,
};

const TAB_LABELS: Record<BillRunsTab, string> = { bills: 'Bills', missing: 'Missing', reports: 'Reports', insights: 'Insights' };
const TAB_METRICS: Record<BillRunsTab, MetricGroup[]> = {
  bills: ['outcome', 'progress', 'assigned'],
  missing: ['coverage'],
  reports: ['send', 'progress'],
  insights: [],
};

type Fetched = { months: string[]; mode: BillRunsView['mode']; includeArchived: boolean };
const fetchKey = (f: Fetched) => `${f.months.join(',')}|${f.mode}|${f.includeArchived ? 1 : 0}`;

export function BillRunsPage() {
  const [params, setParams] = useSearchParams();
  const view = useMemo(() => readBillRunsView(params), [params]);
  const setView = useCallback((patch: Partial<BillRunsView>) => {
    setParams(writeBillRunsView({ ...view, ...patch }), { replace: true });
  }, [view, setParams]);
  const setSite = (patch: Partial<BillRunSiteFilters>) => setView({ site: { ...view.site, ...patch } });
  const setConditions = (patch: Partial<BillRunConditions>) => setView({ conditions: { ...view.conditions, ...patch } });

  // Month picker values: the URL's, else the last completed month (not fetched until Fetch).
  const from = view.from || lastCompletedMonth();
  const to = view.to || from;
  const months = monthRange(from, to);
  const tooMany = months.length > MAX_NMI_MONTHS;
  const wanted: Fetched = { months, mode: view.mode, includeArchived: view.conditions.includeArchived };

  const [data, setData] = useState<BillRunsData | null>(null);
  const [fetched, setFetched] = useState<Fetched | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unmatched, setUnmatched] = useState<number | null>(null);

  const load = useCallback(async (f: Fetched) => {
    if (f.months.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      // Site filters apply instantly in the browser, so the fetch is scoped by months only.
      const res = await billRunsService.fetch({
        months: f.months, mode: f.mode, includeArchived: f.includeArchived,
        siteFilters: { q: '', entity: '', retailer: [], distributor: '', state: '', tariff: '' },
      });
      if (res.success && res.data) { setData(res.data); setFetched(f); }
      else setError(res.error ?? 'Could not load bills');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load bills');
    } finally {
      setLoading(false);
    }
  }, []);

  const doFetch = () => {
    // Pin the picked months into the URL, so the link reproduces the fetch.
    if (!view.from || !view.to) setView({ from, to });
    void load(wanted);
  };
  const refetch = useCallback(() => { if (fetched) void load(fetched); }, [fetched, load]);

  // A bill refreshed elsewhere (a site page in another tab, the Bills list) is
  // not in the rows fetched here: re-read on coming back, so the inline report
  // shows what was done there rather than what was true at Fetch.
  useEffect(() => {
    window.addEventListener('focus', refetch);
    return () => window.removeEventListener('focus', refetch);
  }, [refetch]);

  // Opened from a link that already names months: fetch straight away (FR-1).
  useEffect(() => {
    if (view.from && view.to) void load({ months: monthRange(view.from, view.to), mode: view.mode, includeArchived: view.conditions.includeArchived });
    void billEmailService.getInboxCounts().then((r) => { if (r.success && r.data) setUnmatched(r.data.unmatched); });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on open
  }, []);

  const stale = !!fetched && fetchKey(fetched) !== fetchKey(wanted);

  // Bill run condition (from a "reports of this run" link): the run's bill ids.
  const [runBillIds, setRunBillIds] = useState<ReadonlySet<string> | null>(null);
  useEffect(() => {
    const runId = view.conditions.billRunId;
    if (!runId) { setRunBillIds(null); return; }
    let mounted = true;
    void bulkReportService.runReportBillIds(runId).then((r) => {
      if (mounted) setRunBillIds(r.success && r.data ? new Set(r.data.billIds) : new Set());
    });
    return () => { mounted = false; };
  }, [view.conditions.billRunId]);

  const bills = useMemo(() => data?.bills ?? [], [data]);
  const reportFor = useBillReports(bills, view.tolerance);
  const verdictFor = useCallback((bill: BvBill) => reportFor(bill)?.verdict ?? null, [reportFor]);

  const rows: BillRunRow[] = useMemo(() => {
    if (!data || !fetched) return [];
    return buildBillRunRows({
      bills: data.bills.filter((b) => matchesRunSiteFilters(b, view.site)),
      emails: data.emails, queue: data.queue, analyses: data.analyses, sendHistory: data.sendHistory,
      sites: filterRunSites(data.sites, view.site),
      tags: data.tags,
      lastBillTo: data.lastBillTo,
    }, fetched.mode, fetched.months, verdictFor);
  }, [data, fetched, view.site, verdictFor]);

  const duplicateBills = rows.filter((r) => r.kind === 'bill' && r.board.twins.count > 0).length;

  // Mismatch drivers: the bills that pass every condition but the driver
  // itself, once each (a bill spanning two months is two rows).
  const drivers = useMemo(
    () => driversFromRows(rows, reportFor, (r) => matchesBillRunConditions(r, view.conditions, { runBillIds, skip: ['driver'] })),
    [rows, view.conditions, runBillIds, reportFor],
  );
  // A driver no longer in the top list (filters changed) matches nothing, visibly, rather than silently clearing.
  const driverBillIds = useMemo(() => {
    if (!view.conditions.driver) return null;
    return new Set(drivers.recurring.find((d) => d.key === view.conditions.driver)?.billIds ?? []);
  }, [drivers, view.conditions.driver]);

  // Default order first; a column sort reorders it, with ties left in that order.
  // Sorting here, not per page, so paging, tabs and the CSV export all follow it.
  const filtered = useMemo(
    () => sortBillRunRows(
      rows.filter((r) => matchesBillRunConditions(r, view.conditions, { runBillIds, driverBillIds })).sort(compareBillRunRows),
      view.sort,
    ),
    [rows, view.conditions, view.sort, runBillIds, driverBillIds],
  );
  const metrics = useMemo(
    () => computeMetrics(rows, view.conditions, { runBillIds, driverBillIds }),
    [rows, view.conditions, runBillIds, driverBillIds],
  );

  // Tabs (US: Bills / Missing / Reports): one fetch and one set of filters,
  // each tab showing its own slice of the matching rows.
  const tabCounts = useMemo(() => {
    const c: Record<BillRunsTab, number> = { bills: 0, missing: 0, reports: 0, insights: 0 };
    for (const r of filtered) for (const t of BILL_RUNS_TABS) if (IN_TAB[t](r)) c[t]++;
    return c;
  }, [filtered]);
  const tabRows = useMemo(() => filtered.filter(IN_TAB[view.tab]), [filtered, view.tab]);

  const options = useMemo(() => siteFilterOptions([
    ...bills,
    ...(data?.sites ?? []).map((s) => ({ site: s as unknown as BvBill['site'] })),
  ]), [bills, data]);

  // Paging and selection (by row key).
  const [page, setPage] = useState(1);
  // On a new Fetch, not on a re-read of the same one — the focus re-read above
  // must not throw the user back to page 1.
  useEffect(() => { setPage(1); }, [params, fetched]);
  const lastPage = Math.max(1, Math.ceil(tabRows.length / PAGE_SIZE));
  const safePage = Math.min(page, lastPage);
  const shown = tabRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const tabKeys = useMemo(() => new Set(tabRows.map((r) => r.key)), [tabRows]);
  // A filter change drops selected rows that are no longer shown.
  useEffect(() => {
    setSelected((cur) => {
      const next = new Set([...cur].filter((k) => tabKeys.has(k)));
      return next.size === cur.size ? cur : next;
    });
  }, [tabKeys]);
  const toggle = (key: string) => setSelected((cur) => {
    const next = new Set(cur);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const pageKeys = shown.map((r) => r.key);
  const allMatching = selected.size > 0 && selected.size === tabKeys.size;
  const pageSelected = pageKeys.length > 0 && pageKeys.every((k) => selected.has(k));
  const headerState = allMatching ? 'all' : pageSelected ? 'page' : selected.size > 0 ? 'some' : 'none';
  const onHeaderClick = () => {
    if (allMatching) setSelected(new Set());
    else if (pageSelected) setSelected(new Set(tabKeys));
    else setSelected(new Set(pageKeys));
  };
  const clearSelection = () => setSelected(new Set());

  const selectedRows = tabRows.filter((r) => selected.has(r.key));
  const selectedBills = selectedRows.flatMap((r) => (r.kind === 'bill' ? [r.board] : []));
  const uniqueBills = [...new Map(selectedBills.map((b) => [b.bill.id, b])).values()];
  const selectedMissing = selectedRows.flatMap((r) => (r.kind === 'missing' && r.coverage !== 'covered' ? [r] : []));
  // Sending exactly as Bulk Customer Report: only reports canSend allows.
  const sendableBills = uniqueBills
    .filter((b) => canSend(b.bill, data?.sendHistory[b.bill.id] ?? NO_SENDS, verdictFor(b.bill)))
    .map((b) => b.bill);
  // Every matching row on the Reports tab that Send selected would email, as
  // Bulk Customer Report's "Select all not yet sent".
  const sendableKeys = view.tab === 'reports'
    ? tabRows.filter((r) => r.kind === 'bill' && canSend(r.board.bill, data?.sendHistory[r.board.bill.id] ?? NO_SENDS, verdictFor(r.board.bill))).map((r) => r.key)
    : [];
  const allSendableSelected = sendableKeys.length > 0 && sendableKeys.every((k) => selected.has(k));
  const toggleAllSendable = () => setSelected(allSendableSelected ? new Set() : new Set(sendableKeys));

  const { saving: savingStep, error: stepError, setStep } = useWorkflowStep(refetch);
  const [action, setAction] = useState<BoardAction | null>(null);
  const [bulkSendOpen, setBulkSendOpen] = useState(false);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [sendOne, setSendOne] = useState<{ bill: BvBill; figures: ReportFigures } | null>(null);
  const [batchesKey, setBatchesKey] = useState(0);
  const [sending, setSending] = useState(0);
  const [tagError, setTagError] = useState<string | null>(null);
  const [tagging, setTagging] = useState(false);
  const [actionNotice, setActionNotice] = useState<string | null>(null);

  const tagSelected = async (tag: MissingTag) => {
    setTagging(true);
    setTagError(null);
    const res = await missingBillService.setTag(selectedMissing.map((r) => ({ siteId: r.siteId, month: r.month })), tag, null);
    setTagging(false);
    if (!res.success) setTagError(res.error ?? 'Could not tag the missing months');
    clearSelection();
    refetch();
  };

  const exportName = fetched ? `bill-runs-${fetched.months[0]}${fetched.months.length > 1 ? `_${fetched.months[fetched.months.length - 1]}` : ''}` : 'bill-runs';
  const exportCsv = () => {
    // downloadCsv adds the .csv extension.
    downloadCsv(billRunsCsv(tabRows), exportName);
  };

  // This tab's rows, as the CSV has them, plus a Summary sheet of the metric cards.
  const exportTabXlsx = () => {
    const ms = fetched?.months ?? [];
    const period = ms.length > 1 ? `${monthLabel(ms[0])} – ${monthLabel(ms[ms.length - 1])}` : ms.length ? monthLabel(ms[0]) : '';
    const summary = billRunsSummaryRows({
      period,
      tab: TAB_LABELS[view.tab],
      rowCount: tabRows.length,
      metrics,
      dollars: outcomeDollars(rows, view.conditions, { runBillIds, driverBillIds }),
    });
    exportMultiSheetXLSX(
      [
        { name: 'Summary', rows: summary },
        { name: TAB_LABELS[view.tab], rows: billRunsSheetRows(tabRows) },
      ],
      `${exportName}-${view.tab}`,
    );
  };

  // The client's MultiSite Bill Validation Detail Report layout, plus a Summary
  // sheet: the bills on this tab, after every filter, once each.
  const exportXlsx = () => {
    const seen = new Set<string>();
    const out = tabRows.flatMap((r) => {
      if (r.kind !== 'bill' || seen.has(r.board.bill.id)) return [];
      seen.add(r.board.bill.id);
      return [{ bill: r.board.bill, report: reportFor(r.board.bill) }];
    });
    const ms = fetched?.months ?? [];
    const label = ms.length > 1 ? `${monthLabel(ms[0])} – ${monthLabel(ms[ms.length - 1])}` : ms.length ? monthLabel(ms[0]) : '';
    exportMultiSheetXLSX(
      [
        { name: 'Summary', rows: summaryRows(label, monthMetrics(out), metrics.coverage.total) },
        { name: 'Multiple Sites', rows: out.map((o) => detailRow(o.bill, o.report)) },
      ],
      `MultiSite Bill Validation Detail Report - ${label}`,
    );
  };

  const v = view.conditions.variance;
  const setVariance = (patch: Partial<{ minDollars: number | null; minPct: number | null; direction: VarianceDirection }>) => {
    const next = { minDollars: v?.minDollars ?? null, minPct: v?.minPct ?? null, direction: v?.direction ?? 'either', ...patch };
    const empty = next.minDollars == null && next.minPct == null && next.direction === 'either';
    setConditions({ variance: empty ? null : next });
  };
  const numOrNull = (s: string) => (s.trim() === '' || !Number.isFinite(Number(s)) ? null : Math.max(0, Number(s)));
  const lastRun = data?.lastRun ?? null;

  return (
    <div data-testid="bill-runs2-page" className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bill Runs</h1>
          <p className="text-sm text-gray-500 mt-1">
            Every enrolled site for the months you pick: the bills that arrived, the months still missing,
            their status, and their customer reports.
          </p>
          {lastRun && (
            <p data-testid="bill-runs2-last-run" className="text-xs text-gray-500 mt-1">
              Last bill run {new Date(lastRun.started_at).toLocaleString('en-AU')} — {lastRun.status}
              {lastRun.finished_at && ` in ${runDurationLabel(lastRun.started_at, lastRun.finished_at)}`}
            </p>
          )}
        </div>
        {fetched && (
          <Button variant="outline" size="sm" className="gap-1.5" onClick={refetch} disabled={loading}>
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} /> Refresh
          </Button>
        )}
      </div>

      {/* 1. Months and how bills are assigned to them, then Fetch. */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
        <label className="text-xs text-gray-600">From
          <input data-testid="bill-runs2-from" type="month" value={from} max={to}
            onChange={(e) => setView({ from: e.target.value, to: to < e.target.value ? e.target.value : to })}
            className="block h-8 mt-0.5 rounded-md border border-gray-200 px-2 text-sm" />
        </label>
        <label className="text-xs text-gray-600">To
          <input data-testid="bill-runs2-to" type="month" value={to} min={from}
            onChange={(e) => setView({ from, to: e.target.value })}
            className="block h-8 mt-0.5 rounded-md border border-gray-200 px-2 text-sm" />
        </label>
        <div className="text-xs text-gray-600">Month by
          <div className="mt-0.5 inline-flex rounded-md border border-gray-200 bg-white p-0.5 ml-0 block">
            {([['period', 'Bill period'], ['end', 'Period end date']] as const).map(([m, label]) => (
              <button key={m} type="button" data-testid={`bill-runs2-mode-${m}`} data-active={view.mode === m}
                onClick={() => setView({ mode: m })}
                className={cn('px-2.5 py-1 rounded text-xs font-medium', view.mode === m ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100')}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-600 pb-1.5">
          <input data-testid="bill-runs2-archived" type="checkbox" checked={view.conditions.includeArchived}
            onChange={(e) => setConditions({ includeArchived: e.target.checked })} />
          Show archived
        </label>
        <Button data-testid="bill-runs2-fetch" size="sm" className="gap-1.5" onClick={doFetch} disabled={loading || tooMany || months.length === 0}>
          <Search className="h-3.5 w-3.5" /> Fetch
        </Button>
        {tooMany && <p className="text-xs text-red-600 pb-1.5">Pick at most {MAX_NMI_MONTHS} months.</p>}
        {stale && !loading && <p data-testid="bill-runs2-stale" className="text-xs text-amber-700 pb-1.5">Months changed — Fetch to update.</p>}
      </div>

      {/* 2. Site filters and conditions: instant, no re-fetch. */}
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="bill-runs2-search" value={view.site.q} placeholder="Search NMI or entity…"
          onChange={(e) => setSite({ q: e.target.value })}
          className="h-8 w-52 rounded-md border border-gray-200 px-2.5 text-xs" />
        {([['entity', 'Entity'], ['distributor', 'Distributor'], ['state', 'State'], ['tariff', 'Tariff']] as const).map(([k, label]) => (
          <select key={k} data-testid={`bill-runs2-${k}`} value={view.site[k]} onChange={(e) => setSite({ [k]: e.target.value })}
            className={cn('h-8 rounded-md border bg-white px-2 text-xs max-w-[11rem]', view.site[k] ? 'border-blue-300 text-blue-800' : 'border-gray-200 text-gray-700')}>
            <option value="">{label}: Any</option>
            {options[k].map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        <MultiCheck testId="bill-runs2-retailer" label="Retailer" options={options.retailer} value={view.site.retailer} onChange={(retailer) => setSite({ retailer })} />
        <MultiCheck testId="bill-runs2-status-filter" label="Status" options={BILL_RUN_STATUS_ORDER} labels={BILL_RUN_STATUS_LABELS} value={view.conditions.status} onChange={(status) => setConditions({ status })} />
        <MultiCheck testId="bill-runs2-details-filter" label="Details" options={BILL_RUN_DETAIL_ORDER} labels={BILL_RUN_DETAIL_LABELS} value={view.conditions.details} onChange={(details) => setConditions({ details })} />
        <MultiCheck testId="bill-runs2-assigned-filter" label="Assigned" options={ASSIGNED_ORDER} labels={ASSIGNED_LABELS} value={view.conditions.assigned} onChange={(assigned) => setConditions({ assigned })} />
        <MultiCheck testId="bill-runs2-send-filter" label="Send" options={SEND_STATUS_ORDER} labels={SEND_STATUS_LABELS} value={view.conditions.send} onChange={(send) => setConditions({ send })} />
        <MultiCheck testId="bill-runs2-coverage-filter" label="Coverage" options={COVERAGE_ORDER} labels={COVERAGE_LABELS} value={view.conditions.coverage} onChange={(coverage) => setConditions({ coverage })} />
        <MultiCheck testId="bill-runs2-enrolment-filter" label="Enrolment" options={ENROLMENT_ORDER} labels={ENROLMENT_LABELS} value={view.conditions.enrolment} onChange={(enrolment) => setConditions({ enrolment })} />
        <label className="flex items-center gap-1 text-xs text-gray-600" title="A bill within this % of the expected invoice (or $0.50) is Matched. 5% is the floor.">
          Match within ±
          <select data-testid="bill-runs2-tolerance" value={view.tolerance}
            onChange={(e) => setView({ tolerance: matchTolerance(e.target.value) })}
            className={cn('h-8 rounded-md border bg-white px-1.5', view.tolerance !== DEFAULT_MATCH_TOLERANCE_PCT ? 'border-blue-300 text-blue-800' : 'border-gray-200')}>
            {MATCH_TOLERANCE_OPTIONS.map((t) => <option key={t} value={t}>{t}%</option>)}
          </select>
        </label>
        <div className="flex items-center gap-1 text-xs text-gray-600">
          Variance ≥
          <input data-testid="bill-runs2-var-min" type="number" min={0} placeholder="$" value={v?.minDollars ?? ''}
            onChange={(e) => setVariance({ minDollars: numOrNull(e.target.value) })} className="h-8 w-20 rounded-md border border-gray-200 px-2" />
          <input data-testid="bill-runs2-var-pct" type="number" min={0} placeholder="%" value={v?.minPct ?? ''}
            onChange={(e) => setVariance({ minPct: numOrNull(e.target.value) })} className="h-8 w-16 rounded-md border border-gray-200 px-2" />
          <select data-testid="bill-runs2-var-dir" value={v?.direction ?? 'either'} onChange={(e) => setVariance({ direction: e.target.value as VarianceDirection })}
            className="h-8 rounded-md border border-gray-200 bg-white px-1.5">
            <option value="either">Either</option><option value="over">Over</option><option value="under">Under</option>
          </select>
        </div>
        {view.conditions.billRunId && (
          <span data-testid="bill-runs2-run-chip" className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs text-blue-800">
            One bill run
            <button type="button" aria-label="Show every bill run" onClick={() => setConditions({ billRunId: null })}><X className="h-3 w-3" /></button>
          </span>
        )}
        {view.conditions.driver && (
          <span data-testid="bill-runs2-driver-chip" className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs text-blue-800">
            Recurring difference
            <button type="button" aria-label="Clear recurring difference" onClick={() => setConditions({ driver: null })}><X className="h-3 w-3" /></button>
          </span>
        )}
        {view.conditions.neverBilled && (
          <span data-testid="bill-runs2-never-chip" className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs text-blue-800">
            Never billed
            <button type="button" aria-label="Clear never billed" onClick={() => setConditions({ neverBilled: false })}><X className="h-3 w-3" /></button>
          </span>
        )}
        <Button variant="ghost" size="sm" data-testid="bill-runs2-clear"
          onClick={() => setView({ site: EMPTY_VIEW.site, conditions: { ...EMPTY_VIEW.conditions, includeArchived: view.conditions.includeArchived } })}>
          Clear all
        </Button>
      </div>


      {!!data?.partial.length && (
        <p data-testid="bill-runs-partial" className="text-xs text-amber-700">Some details could not be loaded ({data.partial.join('; ')}) — statuses that depend on them may be wrong. Refresh before acting.</p>
      )}
      {data?.sendHistoryError && (
        <p className="text-xs text-red-600">Send status could not be loaded ({data.sendHistoryError}) — sent reports may show as not sent. Refresh before sending.</p>
      )}


      {!fetched && !loading && !error && (
        <div data-testid="bill-runs2-prompt" className="text-center py-16 text-gray-400">Pick the months and press Fetch.</div>
      )}

      {loading && !data && <div data-testid="bill-runs2-loading" className="text-center py-16 text-gray-400">Loading…</div>}

      {error && (
        <div data-testid="bill-runs2-error" className="flex flex-col items-center gap-2 py-12 text-red-500">
          <AlertCircle className="h-5 w-5" />
          <p className="text-sm">{error}</p>
          <Button variant="outline" size="sm" onClick={() => void load(fetched ?? wanted)}>Retry</Button>
        </div>
      )}

      {data && fetched && !error && (<>
        <div role="tablist" className="flex gap-1 border-b border-gray-200">
          {BILL_RUNS_TABS.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={view.tab === t}
              data-testid={`bill-runs2-tab-${t}`}
              onClick={() => { setSelected(new Set()); setView({ tab: t }); }}
              className={cn(
                '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                view.tab === t ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800',
              )}
            >
              {TAB_LABELS[t]}{t !== 'insights' && <span className="text-xs text-gray-400"> ({tabCounts[t].toLocaleString('en-AU')})</span>}
            </button>
          ))}
        </div>

        {/* Mounted on every tab, shown on Insights: its polling refreshes the rows mid-send. */}
        <div className={view.tab === 'insights' ? 'space-y-3' : 'hidden'}>
          <BillRunsBatches
            refreshKey={batchesKey}
            onProgress={refetch}
            onActive={setSending}
            onSendAgain={(billId) => {
              // Only a bill on this fetch can be re-sent from here, as on Bulk Customer Report.
              const b = bills.find((x) => x.id === billId);
              const figures = b ? reportFigures(b) : null;
              if (b && figures) setSendOne({ bill: b, figures });
            }}
          />
          <MismatchDriversPanel
            drivers={drivers}
            activeDriver={view.conditions.driver}
            activeRetailers={view.site.retailer}
            // A driver or retailer picked here filters the Bills tab.
            onDriver={(driver) => setView({ tab: 'bills', conditions: { ...view.conditions, driver } })}
            onRetailer={(r) => setView({ tab: 'bills', site: { ...view.site, retailer: view.site.retailer.includes(r) ? view.site.retailer.filter((x) => x !== r) : [...view.site.retailer, r] } })}
          />
        </div>

        {view.tab !== 'insights' && (
          // Actions first and pinned, so expanding NMI rows never pushes them off screen.
          <div className="sticky top-0 z-20 -mx-1 space-y-2 bg-white/95 px-1 py-2 backdrop-blur">
            {view.tab === 'reports' && tabRows.length > 0 && (
              <div data-testid="bill-runs2-send-toolbar" className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                  <input
                    data-testid="bill-runs2-select-sendable"
                    type="checkbox"
                    disabled={sendableKeys.length === 0}
                    checked={allSendableSelected}
                    ref={(el) => { if (el) el.indeterminate = !allSendableSelected && sendableKeys.some((k) => selected.has(k)); }}
                    onChange={toggleAllSendable}
                  />
                  Select all not yet sent ({sendableKeys.length})
                </label>
                <span className="text-xs text-gray-400">Narrow with the filters first. Reports already sent are skipped.</span>
                <Button data-testid="bill-runs2-action-send" size="sm" className="ml-auto gap-1.5 bg-emerald-600 hover:bg-emerald-700" disabled={sendableBills.length === 0} onClick={() => setBulkSendOpen(true)}>
                  <Send className="h-3.5 w-3.5" /> Send selected ({sendableBills.length})
                </Button>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <span data-testid="bill-runs2-count" className="text-xs text-gray-500">
                {selected.size > 0 ? `${selected.size} selected (of ${tabRows.length} matching)` : `${tabRows.length} rows`}
              </span>
              {selected.size > 0 && <Button variant="ghost" size="sm" onClick={clearSelection}>Clear</Button>}
              <div className="ml-auto flex flex-wrap items-center gap-2">
                {/* Bills sharing a period with another active bill (as on Bulk Bill Run). */}
                {view.tab === 'bills' && duplicateBills > 0 && (
                  <Button data-testid="bill-runs2-fix-duplicates" variant="outline" size="sm" className="gap-1.5" onClick={() => setDuplicatesOpen(true)}>
                    <Copy className="h-3.5 w-3.5" /> Fix duplicates ({duplicateBills} bills)
                  </Button>
                )}
                {view.tab === 'bills' && uniqueBills.length > 0 && (<>
                  <AssignedStatusSelect testId="bill-runs2-action-assigned" disabled={savingStep}
                    onSet={(step) => void setStep(uniqueBills.map((b) => b.bill.id), step)} />
                  <Button data-testid="bill-runs2-action-parse" variant="outline" size="sm" className="gap-1.5" onClick={() => setAction('parse')}>
                    <FileSearch className="h-3.5 w-3.5" /> Parse invoices
                  </Button>
                  <Button data-testid="bill-runs2-action-run" variant="outline" size="sm" className="gap-1.5" onClick={() => setAction('run')}>
                    <Gauge className="h-3.5 w-3.5" /> Run bill run
                  </Button>
                </>)}
                {/* Mounted for the whole tab: its archive dialog outlives the selection it clears. */}
                {view.tab === 'bills' && (
                  <BillRunsBillActions
                    bills={uniqueBills.map((b) => b.bill)}
                    onNotice={setActionNotice}
                    onChanged={() => { clearSelection(); refetch(); }}
                  />
                )}
                {view.tab === 'missing' && selectedMissing.length > 0 && (
                  <select data-testid="bill-runs2-action-tag" value="" disabled={tagging}
                    onChange={(e) => { if (e.target.value) void tagSelected(e.target.value as MissingTag); }}
                    className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs">
                    <option value="">Tag {selectedMissing.length} missing month{selectedMissing.length === 1 ? '' : 's'}…</option>
                    {MISSING_TAGS.map((t) => <option key={t} value={t}>{MISSING_TAG_LABELS[t]}</option>)}
                  </select>
                )}
                {view.tab === 'reports' && (
                  <Button data-testid="bill-runs2-send-progress" variant="outline" size="sm" className="gap-1.5" onClick={() => setView({ tab: 'insights' })}>
                    {sending > 0 ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    {sending > 0 ? `Sending (${sending} batch${sending === 1 ? '' : 'es'})` : 'Send progress'}
                  </Button>
                )}
                {view.tab === 'bills' && (
                  <Button data-testid="bill-runs2-drivers-link" variant="outline" size="sm" className="gap-1.5" onClick={() => setView({ tab: 'insights' })}>
                    <Gauge className="h-3.5 w-3.5" /> Mismatch drivers{view.conditions.driver ? ' (filtering)' : ''}
                  </Button>
                )}
                <Button data-testid="bill-runs2-export" variant="outline" size="sm" className="gap-1.5" onClick={exportCsv} disabled={tabRows.length === 0}>
                  <Download className="h-3.5 w-3.5" /> Export CSV
                </Button>
                <Button data-testid="bill-runs2-export-tab-xlsx" variant="outline" size="sm" className="gap-1.5" onClick={exportTabXlsx} disabled={tabRows.length === 0}
                  title="This tab's rows, with a Summary sheet of the counts">
                  <Download className="h-3.5 w-3.5" /> Export XLSX <NewBadge />
                </Button>
                {view.tab !== 'missing' && (
                  <Button data-testid="bill-runs2-export-xlsx" variant="outline" size="sm" className="gap-1.5" onClick={exportXlsx} disabled={tabRows.length === 0}
                    title="MultiSite Bill Validation Detail Report layout, with a Summary sheet">
                    <Download className="h-3.5 w-3.5" /> Export MultiSite XLSX
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
        {view.tab !== 'insights' && <BillRunsMetrics show={TAB_METRICS[view.tab]} metrics={metrics} conditions={view.conditions} onConditions={setConditions} />}
        {view.tab === 'missing' && unmatched !== null && unmatched > 0 && (
          <Link data-testid="bill-runs2-unmatched" to="/bill-validation/inbox?status=unmatched" className="block text-xs text-amber-800 hover:underline">
            {unmatched} unmatched invoice{unmatched === 1 ? '' : 's'} in the Inbox — check before chasing a retailer
          </Link>
        )}

        {(stepError || tagError) && <p data-testid="bill-runs2-action-error" className="text-sm text-red-600">{stepError ?? tagError}</p>}
        {view.tab === 'bills' && actionNotice && <p data-testid="bill-runs2-action-notice" className="text-xs text-blue-700">{actionNotice}</p>}

        {view.tab === 'insights' ? null : tabRows.length === 0 ? (
          <div data-testid="bill-runs2-empty" className="text-center py-16 text-gray-400">
            {rows.length === 0 ? 'No sites or bills for these months.' : 'No rows match the current filters.'}
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <BillRunsTable
              rows={shown}
              isSelected={(k) => selected.has(k)}
              onToggle={toggle}
              headerState={headerState}
              onHeaderClick={onHeaderClick}
              savingStep={savingStep}
              onSetStep={(ids, step) => void setStep(ids, step)}
              onSendOne={(bill, figures) => setSendOne({ bill, figures })}
              onTagsChanged={refetch}
              onBillsChanged={refetch}
              sort={view.sort}
              onSort={(key) => setView({ sort: nextSort(view.sort, key) })}
            />
            <div className="flex items-center justify-between px-4 py-2.5 border-t text-xs text-gray-500">
              <span data-testid="bill-runs2-page-info">{tabRows.length} rows · page {safePage} of {lastPage}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}><ChevronLeft className="h-4 w-4" /> Prev</Button>
                <Button variant="outline" size="sm" disabled={safePage >= lastPage} onClick={() => setPage(safePage + 1)}>Next <ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        )}
      </>)}

      {duplicatesOpen && data && (
        <DuplicatePeriodsDialog
          bills={data.bills}
          onClose={(changed) => { setDuplicatesOpen(false); if (changed) refetch(); }}
        />
      )}

      {action && (
        <BoardActionDialog
          action={action}
          rows={uniqueBills}
          onClose={(changed) => { setAction(null); if (changed) { clearSelection(); refetch(); } }}
        />
      )}

      {bulkSendOpen && (
        <BulkSendReportsDialog
          open
          bills={sendableBills}
          onClose={(queuedAny) => {
            setBulkSendOpen(false);
            clearSelection();
            if (queuedAny) setBatchesKey((k) => k + 1);
            refetch();
          }}
        />
      )}

      {sendOne && (
        <SendBillReportDialog
          open
          bill={sendOne.bill}
          shadowResult={sendOne.figures.shadowResult}
          actualResult={sendOne.figures.actualResult}
          gstExcluded={sendOne.figures.gstExcluded}
          onClose={() => { setSendOne(null); refetch(); }}
        />
      )}
    </div>
  );
}
